import { NavLink, useLocation } from 'react-router-dom'
import {
  Home,
  Tag,
  Layers,
  CalendarDays,
  ShoppingCart,
  Warehouse,
  ChevronDown,
  BarChart3,
  Settings,
  X,
  Factory,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useState } from 'react'
import { NAV_ITEMS, type NavChild, type NavItem } from '@/config/navigation'
import { useCan } from '@/features/auth/AuthContext'

const iconMap: Record<string, LucideIcon> = {
  home: Home,
  sales: Tag,
  planning: CalendarDays,
  production: Factory,
  purchasing: ShoppingCart,
  warehouse: Warehouse,
  catalog: Layers,
  reports: BarChart3,
  admin: Settings,
}

function childIsActive(child: NavChild, pathname: string, parentPath: string) {
  if (!child.path || child.disabled) return false
  if (child.path === parentPath) return pathname === child.path
  return pathname === child.path || pathname.startsWith(`${child.path}/`)
}

function groupShouldOpen(item: NavItem, pathname: string) {
  if (item.match(pathname)) return true
  return Boolean(
    item.children?.some((c) => childIsActive(c, pathname, item.path)),
  )
}

export interface SidebarProps {
  isMobile?: boolean
  onClose?: () => void
}

export function Sidebar({ isMobile = false, onClose }: SidebarProps = {}) {
  const location = useLocation()
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {}
    for (const item of NAV_ITEMS) {
      if (item.children?.length) {
        init[item.id] = groupShouldOpen(item, location.pathname)
      }
    }
    return init
  })

  const canAdmin = useCan('admin.users')

  return (
    <aside
      className={`flex h-full ${isMobile ? 'w-full' : 'w-[var(--sidebar-width)]'} shrink-0 flex-col bg-[var(--color-sidebar)] text-[var(--color-sidebar-text)]`}
      aria-label="Main navigation"
    >
      <div className="flex h-[var(--topbar-height)] items-center gap-2.5 border-b border-white/10 px-4">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-[var(--radius-sm)] bg-white p-0.5">
          <img
            src="/logo-mocphat.jpg"
            alt="Mộc Phát"
            className="h-full w-full object-contain"
          />
        </div>
        <div className="min-w-0 leading-tight">
          <div className="truncate text-sm font-bold text-white">MỘC PHÁT</div>
          <div className="text-xs font-semibold tracking-wider text-[var(--color-sidebar-muted)]">
            FURNITURE ERP
          </div>
        </div>
        {isMobile && (
          <button
            type="button"
            onClick={onClose}
            className="ml-auto flex h-8 w-8 items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-sidebar-muted)] hover:bg-white/10 hover:text-white"
            aria-label="Đóng menu"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      <nav className="flex-1 space-y-0.5 overflow-y-auto p-2">
        {NAV_ITEMS.map((item) => {
          if (item.id === 'admin' && !canAdmin) {
            return null
          }
          const Icon = iconMap[item.id] ?? Layers
          const active = item.match(location.pathname)
          const hasChildren = Boolean(item.children?.length)
          const expanded = openGroups[item.id] ?? active

          if (item.disabled) {
            return (
              <div
                key={item.id}
                className="flex cursor-not-allowed items-center gap-2.5 rounded-[var(--radius-sm)] px-3 py-2 text-sm text-[var(--color-sidebar-muted)] opacity-40"
                title="Chưa có trang"
                aria-disabled="true"
              >
                <Icon className="h-4 w-4" strokeWidth={1.8} />
                <span>{item.label}</span>
              </div>
            )
          }

          if (!hasChildren) {
            return (
              <NavLink
                key={item.id}
                to={item.path}
                end={item.path === '/'}
                onClick={() => {
                  if (isMobile) onClose?.()
                }}
                className={`flex items-center gap-2.5 rounded-[var(--radius-sm)] px-3 py-2 text-sm transition-colors ${
                  active
                    ? 'bg-[var(--color-sidebar-active)] font-medium text-[var(--color-primary-fixed-dim,#9ed2b0)]'
                    : 'font-normal text-[var(--color-sidebar-muted)] hover:bg-[var(--color-sidebar-hover)] hover:text-white'
                }`}
              >
                <Icon
                  className={`h-4 w-4 ${
                    active
                      ? 'text-[var(--color-primary-fixed-dim,#9ed2b0)]'
                      : 'text-[var(--color-sidebar-muted)]'
                  }`}
                  strokeWidth={1.8}
                />
                <span>{item.label}</span>
              </NavLink>
            )
          }

          return (
            <div key={item.id} className="space-y-0.5">
              <button
                type="button"
                onClick={() =>
                  setOpenGroups((prev) => ({ ...prev, [item.id]: !expanded }))
                }
                className={`flex w-full items-center gap-2.5 rounded-[var(--radius-sm)] px-3 py-2 text-sm transition-colors ${
                  active
                    ? 'bg-[var(--color-sidebar-active)] font-medium text-[var(--color-primary-fixed-dim,#9ed2b0)]'
                    : 'font-normal text-[var(--color-sidebar-muted)] hover:bg-[var(--color-sidebar-hover)] hover:text-white'
                }`}
              >
                <Icon
                  className={`h-4 w-4 ${
                    active
                      ? 'text-[var(--color-primary-fixed-dim,#9ed2b0)]'
                      : 'text-[var(--color-sidebar-muted)]'
                  }`}
                  strokeWidth={1.8}
                />
                <span className="flex-1 text-left">{item.label}</span>
                <ChevronDown
                  className={`h-3.5 w-3.5 text-[var(--color-sidebar-muted)] transition ${
                    expanded ? 'rotate-0' : '-rotate-90'
                  }`}
                />
              </button>
              {expanded
                ? item.children!.map((child) => {
                    if (child.disabled || !child.path) {
                      return (
                        <div
                          key={child.id}
                          className="ml-3 cursor-not-allowed rounded-[var(--radius-sm)] px-3 py-1.5 text-xs text-[var(--color-sidebar-muted)] opacity-40"
                          title="Chưa có trang"
                          aria-disabled="true"
                        >
                          {child.label}
                        </div>
                      )
                    }
                    const childActive = childIsActive(
                      child,
                      location.pathname,
                      item.path,
                    )
                    return (
                      <NavLink
                        key={child.id}
                        to={child.path}
                        end={child.path === item.path}
                        onClick={() => {
                          if (isMobile) onClose?.()
                        }}
                        className={`ml-3 flex items-center rounded-[var(--radius-sm)] px-3 py-1.5 text-xs transition-colors ${
                          childActive
                            ? 'bg-white/10 font-medium text-white'
                            : 'text-[var(--color-sidebar-muted)] hover:bg-[var(--color-sidebar-hover)] hover:text-white'
                        }`}
                      >
                        {child.label}
                      </NavLink>
                    )
                  })
                : null}
            </div>
          )
        })}
      </nav>
      <div className="border-t border-white/10 px-3 py-2 text-xs text-[var(--color-sidebar-muted)]">
        Bản chạy thử nội bộ
      </div>
    </aside>
  )
}
